// src/styles/breakpoints.ts
import { css } from 'styled-components';
import { stripUnit } from 'polished';

export type BreakpointKey = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type Breakpoints = Record<BreakpointKey, number | string>;

// Default values (tweak to match your design system)
const defaultBreakpoints: Breakpoints = {
    xxs: 0,
    xs: 576,
    sm: 768,
    md: 992,
    lg: 1200,
    xl: 1400,
};

const toNum = (v: number | string) => (typeof v === 'number' ? v : Number(stripUnit(v)));

const mediaUp =
    (size: number) =>
    (...args: Parameters<typeof css>): ReturnType<typeof css> => css`
        @media (min-width: ${size}px) {
            ${css(...args)}
        }
    `;

const mediaDown =
    (size: number) =>
    (...args: Parameters<typeof css>): ReturnType<typeof css> => css`
        @media (max-width: ${size - 1}px) {
            ${css(...args)}
        }
    `;

const mediaBetween =
    (min: number, max: number) =>
    (...args: Parameters<typeof css>): ReturnType<typeof css> => css`
        @media (min-width: ${min}px) and (max-width: ${max - 1}px) {
            ${css(...args)}
        }
    `;

export const makeBreakpoints = (breakpoints: Breakpoints = defaultBreakpoints) => {
    type Fn = (
        ...args: Parameters<typeof css>
    ) => import('styled-components').FlattenInterpolation<import('styled-components').ThemedStyledProps<object, any>>;
    const keys = Object.keys(breakpoints) as BreakpointKey[];

    const nextMap: Record<BreakpointKey, BreakpointKey | null> = {
        xxs: 'xs',
        xs: 'sm',
        sm: 'md',
        md: 'lg',
        lg: 'xl',
        xl: null,
    };

    const breakpointUp = keys.reduce(
        (acc, label) => {
            acc[label] = (...args: Parameters<typeof css>) => mediaUp(toNum(breakpoints[label]))(...args);
            return acc;
        },
        {} as Record<BreakpointKey, Fn>
    );

    const breakpointDown = keys.reduce(
        (acc, label) => {
            const next = nextMap[label];
            acc[label] = (...args: Parameters<typeof css>) => {
                if (!next) return css``; // mirrors the original "default: size = 0" (no-op for 'xl')
                return mediaDown(toNum(breakpoints[next]))(...args);
            };
            return acc;
        },
        {} as Record<BreakpointKey, Fn>
    );

    const breakpointOnly = keys.reduce(
        (acc, label) => {
            acc[label] = (...args: Parameters<typeof css>) => {
                if (label === 'xxs') {
                    return mediaDown(toNum(breakpoints['xs']))(...args);
                }
                if (label === 'xl') {
                    return mediaUp(toNum(breakpoints['xl']))(...args);
                }
                const min = toNum(breakpoints[label]);
                const next = nextMap[label]!;
                const max = toNum(breakpoints[next]);
                return mediaBetween(min, max)(...args);
            };
            return acc;
        },
        {} as Record<BreakpointKey, Fn>
    );

    const breakpointBetween =
        (min: BreakpointKey, max: BreakpointKey) =>
        (...args: Parameters<typeof css>) =>
            mediaBetween(toNum(breakpoints[min]), toNum(breakpoints[max]))(...args);

    return { breakpointUp, breakpointDown, breakpointOnly, breakpointBetween };
};

// Ready-to-use exports with the defaults
export const { breakpointUp, breakpointDown, breakpointOnly, breakpointBetween } = makeBreakpoints();
